/**
 * Error reporting — Phase 10, `docs/01` §Phase 10.
 *
 * Four things go wrong in a browser and until this phase three of them left no
 * trace: a render error (the `<ErrorBoundary>` logged and moved on), an
 * uncaught exception anywhere else, an unhandled promise rejection, and a
 * failure the client caught, handled correctly, and would still want counted.
 * This is one place that sees all four.
 *
 * ## There is no transport, and that is a decision rather than an omission
 *
 * `docs/03` §7 puts **any analytics origin** out of scope for v1, and Phase 10's
 * CSP allow-list is three origins with a script that fails on a fourth — so a
 * third-party reporter is not merely unbuilt, it is prohibited by a check that
 * runs. The same-origin alternative would be an endpoint, and `spec/openapi.yaml`
 * has none: 43 operations, not one of them a place to put a client stack trace.
 * Inventing one here would be inventing a wire contract, which is the spec
 * owner's call (`docs/06` §F30b names exactly what it would take).
 *
 * So what ships is everything up to the wire: capture, redaction, deduplication,
 * a bounded buffer, and a `sink` the transport plugs into when there is one. The
 * default sink is `console.error`, which is what a support conversation reads
 * today and what `e2e/market.spec.ts` already asserts stays quiet.
 *
 * ## Redaction is not optional and runs before anything else
 *
 * This client holds a session key in IndexedDB and signs EIP-712 digests with
 * it. A stack trace or a rejection reason can carry a private key, a signature,
 * a session cookie or a bearer token in its message, and an error report is the
 * one payload nobody reviews before it leaves. `redact()` runs on every string
 * on the way in, so an unredacted value is never in the buffer to begin with —
 * redacting at the sink would leave it sitting in memory for whatever reads
 * `__udErrors` next.
 *
 * ## Deduplication, because one broken render is thousands of errors
 *
 * A component that throws on a `book` frame throws again on the next one, twenty
 * times a second. Counting is the whole difference between a report that says
 * "`<OrderbookLadder>` threw 4,812 times in four minutes" and a buffer holding
 * 4,812 copies of one line with the first real error pushed out of it.
 *
 * No React, no DOM types beyond the two events it listens for, and no
 * `Date.now()` — the clock arrives as a parameter (invariants 7 and 10).
 */

/** What one distinct failure looks like once it has been seen at least once. */
export interface ErrorReport {
  /** `render` | `uncaught` | `unhandledrejection` | `handled`. */
  kind: ErrorKind
  /** `TypeError`, `Error`, or whatever was thrown if it was not an Error. */
  name: string
  /** Redacted, and capped at `MAX_MESSAGE_CHARS`. */
  message: string
  /** Redacted, and capped at `MAX_STACK_FRAMES` frames. */
  stack: string[]
  /** Where the client says it happened — a panel name, a channel, a route. */
  context: string | null
  /** How many times this exact failure has been seen. */
  count: number
  /** Corrected-clock milliseconds of the first and most recent occurrence. */
  firstAtMs: number
  lastAtMs: number
}

export type ErrorKind = 'render' | 'uncaught' | 'unhandledrejection' | 'handled'

/** Distinct failures held. The oldest is dropped, never the newest. */
export const MAX_REPORTS = 50
const MAX_MESSAGE_CHARS = 512
const MAX_STACK_FRAMES = 12

/* -------------------------------------------------------------------------- *
 * redaction
 * -------------------------------------------------------------------------- */

/**
 * Every pattern that could carry a secret out of this client.
 *
 * Ordered longest-match-first, because `HEX_LONG` would otherwise leave the tail
 * of a 66-character key behind after `ADDRESS` had eaten its first 42
 * characters. Each replacement keeps enough shape to be recognisable — a report
 * saying `<address>` is readable and a report saying nothing is not.
 */
const REDACTIONS: readonly [RegExp, string][] = [
  /*
    A private key, a signature, a digest, a token id: `0x` and a long run.

    **41, not 40.** An address is exactly 40 hex characters, so `{40,}` would
    swallow it here and the row below would never fire — every report would say
    `0x<redacted>` and the distinction the next pattern exists to draw would be
    silently dead. Written as `{40,}` first, and the test that allowed either
    label is what hid it.
  */
  [/0x[0-9a-fA-F]{41,}/g, '0x<redacted>'],
  // An address, exactly. Kept distinguishable from the above so a report can
  // still say "a wallet address was involved" without saying which.
  [/0x[0-9a-fA-F]{40}\b/g, '0x<address>'],
  // A JWT, and anything else shaped like one.
  [/\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]+/g, '<jwt>'],
  // `Authorization: Bearer …` — the keyword and its value are separated by a
  // space, not by the colon, which is why this is its own pattern.
  [/\bbearer\s+\S+/gi, 'bearer <redacted>'],
  // `Cookie: ud_session=…`, `X-UD-Signature: …`, `X-UD-Key: …`.
  [/\b(authorization|cookie|ud_session|x-ud-signature|x-ud-key)\s*[:=]\s*\S+/gi, '$1=<redacted>'],
  // A query string can carry a nonce, a cursor or a session hint. The path is
  // the useful half of a URL in a stack trace anyway.
  [/(\?)[^\s"')]+/g, '$1<query>'],
]

export function redact(input: string): string {
  let out = input
  for (const [pattern, replacement] of REDACTIONS) out = out.replace(pattern, replacement)
  return out
}

/* -------------------------------------------------------------------------- *
 * the reporter
 * -------------------------------------------------------------------------- */

export interface ErrorReporterOptions {
  /**
   * Called the first time a distinct failure is seen and never again for it.
   *
   * Once per *distinct* failure rather than once per occurrence: the 4,812th
   * copy of a broken render tells nobody anything, and a sink called at 20 Hz is
   * a second way for the page to fall over.
   */
  sink?: (report: ErrorReport) => void
  /** Corrected-clock milliseconds. Injected — this module owns no clock. */
  now: () => number
}

export class ErrorReporter {
  readonly #reports = new Map<string, ErrorReport>()
  readonly #sink: (report: ErrorReport) => void
  readonly #now: () => number
  /** Occurrences seen, including the ones folded into an existing report. */
  #occurrences = 0

  constructor(options: ErrorReporterOptions) {
    this.#sink = options.sink ?? defaultSink
    this.#now = options.now
  }

  /** Distinct failures, oldest first. */
  reports(): ErrorReport[] {
    return [...this.#reports.values()]
  }

  /** Total occurrences, which is the number that says how bad it is. */
  occurrences(): number {
    return this.#occurrences
  }

  /**
   * Record one failure.
   *
   * `thrown` is `unknown` on purpose: `throw 'nope'` is legal JavaScript, a
   * rejected fetch can reject with a `Response`, and a reporter that assumes an
   * `Error` throws inside the handler that exists to catch throwing.
   */
  report(kind: ErrorKind, thrown: unknown, context?: string | null): ErrorReport {
    this.#occurrences++
    const at = Math.floor(this.#now())
    const { name, message, stack } = describe(thrown)
    const safeContext = context ? redact(context).slice(0, 120) : null

    // The key is what makes two occurrences the same failure: the kind, the
    // constructor, the message and the top frame. Not the whole stack — a React
    // stack differs by key between two renders of the same broken row.
    const key = `${kind}|${name}|${message}|${stack[0] ?? ''}|${safeContext ?? ''}`

    const existing = this.#reports.get(key)
    if (existing) {
      existing.count++
      existing.lastAtMs = at
      return existing
    }

    const report: ErrorReport = {
      kind,
      name,
      message,
      stack,
      context: safeContext,
      count: 1,
      firstAtMs: at,
      lastAtMs: at,
    }
    this.#reports.set(key, report)

    // Drop the oldest, never the newest. A buffer that discards what just
    // happened in favour of what happened four minutes ago is a buffer that is
    // empty of the thing being investigated.
    if (this.#reports.size > MAX_REPORTS) {
      const oldest = this.#reports.keys().next()
      if (!oldest.done) this.#reports.delete(oldest.value)
    }

    try {
      this.#sink(report)
    } catch {
      // A sink that throws must not become the error it is reporting.
    }
    return report
  }

  /** For a test, and for a support conversation that starts "reload it". */
  clear(): void {
    this.#reports.clear()
    this.#occurrences = 0
  }
}

function describe(thrown: unknown): { name: string; message: string; stack: string[] } {
  if (thrown instanceof Error) {
    return {
      name: thrown.name || 'Error',
      message: redact(thrown.message ?? '').slice(0, MAX_MESSAGE_CHARS),
      stack: framesOf(thrown.stack),
    }
  }
  // `throw 'nope'`, a rejected `Response`, `undefined` from an aborted promise.
  const asText = typeof thrown === 'string' ? thrown : safeStringify(thrown)
  return {
    name: thrown === null ? 'null' : typeof thrown,
    message: redact(asText).slice(0, MAX_MESSAGE_CHARS),
    stack: [],
  }
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value) ?? String(value)
  } catch {
    // Circular, or a getter that throws. `String()` on a DOM object is still
    // more informative than dropping the report.
    return Object.prototype.toString.call(value)
  }
}

function framesOf(stack: string | undefined): string[] {
  if (!stack) return []
  return stack
    .split('\n')
    .slice(1, MAX_STACK_FRAMES + 1)
    .map((line) => redact(line.trim()))
    .filter((line) => line !== '')
}

/**
 * `console.error`, in the shape a support conversation can read back.
 *
 * Deliberately one line plus the frames rather than the raw `Error`: a devtools
 * console expands an `Error` into an unredacted `stack` property, which would
 * undo the whole point of `redact()` on the one surface a user is most likely to
 * screenshot.
 */
function defaultSink(report: ErrorReport): void {
  const where = report.context ? ` in ${report.context}` : ''
  console.error(`[uptik] ${report.kind}${where}: ${report.name}: ${report.message}`, report.stack)
}

/* -------------------------------------------------------------------------- *
 * installation
 * -------------------------------------------------------------------------- */

export interface InstalledReporter {
  reporter: ErrorReporter
  uninstall: () => void
}

/**
 * Listen for what the page throws past every `catch` in the client.
 *
 * `capture: false` and no `preventDefault()`: this observes, it does not
 * swallow. An uncaught error must still reach the console and still fail a test
 * that asserts a clean one — a reporter that quietly absorbs errors is how a
 * broken build ships green.
 */
export function installErrorReporting(options: ErrorReporterOptions): InstalledReporter {
  const reporter = new ErrorReporter(options)

  const onError = (event: ErrorEvent) => {
    reporter.report('uncaught', event.error ?? event.message, event.filename || null)
  }
  const onRejection = (event: PromiseRejectionEvent) => {
    reporter.report('unhandledrejection', event.reason, null)
  }

  globalThis.addEventListener?.('error', onError)
  globalThis.addEventListener?.('unhandledrejection', onRejection)

  // The same shape `runtime.ts` uses for `__udRuntime`: a browser test needs to
  // read what was captured, and there is no other way to observe a console.
  ;(globalThis as unknown as { __udErrors?: ErrorReporter }).__udErrors = reporter

  return {
    reporter,
    uninstall: () => {
      globalThis.removeEventListener?.('error', onError)
      globalThis.removeEventListener?.('unhandledrejection', onRejection)
      delete (globalThis as unknown as { __udErrors?: ErrorReporter }).__udErrors
    },
  }
}

/**
 * The reporter the app installed, if it has booted.
 *
 * A module-level lookup rather than a context: `<ErrorBoundary>` is a class
 * component whose whole job is to work when the tree below it does not, and
 * reading a context in `componentDidCatch` is one more thing that can be missing
 * at exactly the wrong moment.
 */
export function currentReporter(): ErrorReporter | null {
  return (globalThis as unknown as { __udErrors?: ErrorReporter }).__udErrors ?? null
}
